import clsx from "clsx";
import type { MissionStepLogItem } from "@/services/api/agentic-v2";
import { Badge } from "@/components/ui/badge/Badge";
import { formatRelativeTime } from "../MissionRow";

function eventLabel(eventType: string): string {
  if (eventType === "mission.step.completed") return "Completed";
  if (eventType === "mission.step.queued") return "Queued";
  return eventType.replace("mission.step.", "").replace(/_/g, " ");
}

function eventBadgeColor(eventType: string): "green" | "slate" {
  if (eventType === "mission.step.completed") return "green";
  return "slate";
}

function actorLabel(item: MissionStepLogItem): string | null {
  if (item.actor_id) return item.actor_id;
  if (item.actor_type === "agent") return "Agent";
  if (item.actor_type === "system") return "System";
  return null;
}

export interface MissionStepLogPanelProps {
  items: MissionStepLogItem[];
  className?: string;
}

export function MissionStepLogPanel({ items, className }: MissionStepLogPanelProps) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-slate-500" data-testid="mission-step-log-empty">
        No steps logged yet.
      </p>
    );
  }

  return (
    <ol className={clsx("space-y-3", className)} data-testid="mission-step-log-list">
      {items.map((item, index) => {
        const actor = actorLabel(item);
        return (
          <li
            key={`${item.step_key}-${item.event_type}-${item.occurred_at}-${index}`}
            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700/60 dark:bg-slate-900/40"
            data-testid={`mission-step-log-row-${item.step_key}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {item.display_name}
                </p>
                <p className="mt-0.5 font-mono text-xs text-slate-500 dark:text-slate-400">
                  {item.step_key}
                </p>
              </div>
              <Badge color={eventBadgeColor(item.event_type)} size="sm">
                {eventLabel(item.event_type)}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
              {item.stage ? <span>{item.stage}</span> : null}
              {item.occurred_at ? (
                <span>{formatRelativeTime(item.occurred_at)}</span>
              ) : null}
              {actor ? <span>{actor}</span> : null}
              {item.outcome ? <span>Outcome: {item.outcome}</span> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
